import { type MouseEvent, useState } from 'react';

import { type ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { formatDistance } from 'date-fns';
import { Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { GetDashboardMlsShareRequestsQuery } from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { formatAddress } from '@modules/_shared/utils/formatting';
import {
  makeRowKeyDownHandler,
  navigateWithModifiers,
} from '@modules/_shared/utils/navigation';
import { useMlsTracking } from '@modules/mls/mlsAnalytics';
import { HANDLE_MLS_SHARE_REQUEST } from '@modules/mls/mlsQueries';
import { ANALYTICS_EVENTS } from '@shared/analytics';
import { useLocale } from '@shared/hooks/locale';

import { DashboardPropertyThumbnail } from '../shared/DashboardPropertyThumbnail';

type DashboardMlsShareRequestsCardItemProps = {
  request: GetDashboardMlsShareRequestsQuery['mls_shares'][number];
  onStatusChange: () => void;
};

export const DashboardMlsShareRequestsCardItem = ({
  request,
  onStatusChange,
}: DashboardMlsShareRequestsCardItemProps) => {
  const { t, dateLocale } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const navigate = useNavigate();
  const { me } = useAppData();
  const { trackMlsAttempt } = useMlsTracking(me);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [handleShareRequestMutation] = useMutation(HANDLE_MLS_SHARE_REQUEST, {
    onError: setErrorLike,
  });

  // Use computed fields for cross-tenant name resolution
  const requestingUserName = request.requester_name ?? '';
  const teamName = request.requester_team_name ?? '';

  const propertyAddress = request.lot?.property
    ? formatAddress(request.lot.property, ', ')
    : request.lot?.title || t('Unknown listing');

  const imageUrl =
    request.lot?.property?.property_images?.[0]?.image?.url || null;

  const timeAgo = formatDistance(new Date(request.created_at), new Date(), {
    locale: dateLocale,
    addSuffix: true,
  });
  const listingPath = request.lot?.id
    ? `/dashboard/listings/${request.lot.id}/marketing/mls-network`
    : null;

  const handleRowClick = (event: MouseEvent<HTMLElement>) => {
    if (listingPath) {
      navigateWithModifiers(event, navigate, listingPath);
    }
  };

  const handleAction = async (
    event: MouseEvent<HTMLElement>,
    status: 'shared' | 'rejected',
  ) => {
    event.stopPropagation();
    if (!me?.id) {
      return;
    }

    const setLoading = status === 'shared' ? setAccepting : setRejecting;
    setLoading(true);
    // Lifecycle (→ PostHog + Customer.io on success only); mirrors the
    // settings-panel event but tagged as the dashboard surface. Failures are
    // tracked PostHog-only. The `onError` snackbar is still surfaced by the
    // mutation hook.
    const outcome = await trackMlsAttempt(
      ANALYTICS_EVENTS.MLS_ACCESS_REQUEST_HANDLED,
      ANALYTICS_EVENTS.MLS_ACCESS_REQUEST_HANDLE_FAILED,
      {
        lot_id: request.lot_id,
        team_id: request.team_id,
        decision: status === 'shared' ? 'approved' : 'rejected',
        source_surface: 'dashboard',
      },
      () =>
        handleShareRequestMutation({
          variables: { requestId: request.id, status, handledByUserId: me.id },
        }),
      { lifecycle: true },
    );
    if (outcome.success) {
      onStatusChange();
    }
    setLoading(false);
  };

  return (
    <>
      <Box
        role={listingPath ? 'button' : undefined}
        tabIndex={listingPath ? 0 : undefined}
        onClick={handleRowClick}
        onKeyDown={makeRowKeyDownHandler(listingPath, navigate)}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
          alignItems: 'center',
          pr: 2,
          minHeight: 64,
          border: `1px solid ${palette.grey[200]}`,
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'grey.300',
          },
          '&:focus-visible': listingPath
            ? {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              }
            : undefined,
        }}
      >
        <DashboardPropertyThumbnail imageUrl={imageUrl} />

        <Box
          sx={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            mr: 1,
          }}
        >
          <Typography variant="body2" noWrap>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {requestingUserName || teamName}
            </Box>
            {requestingUserName && teamName && (
              <>
                <Box
                  component="span"
                  sx={{ color: 'text.disabled', mx: 0.75, userSelect: 'none' }}
                >
                  |
                </Box>
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {teamName}
                </Box>
              </>
            )}
          </Typography>

          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
            }}
          >
            {propertyAddress}
          </Typography>
        </Box>

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            flexWrap: 'nowrap',
          }}
        >
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {timeAgo}
          </Typography>

          <Button
            size="small"
            variant="soft"
            color="error"
            startIcon={<X />}
            loading={rejecting}
            loadingPosition="start"
            disabled={accepting}
            onClick={e => handleAction(e, 'rejected')}
            sx={{ minWidth: 'auto', px: 1.5 }}
          >
            {t('Reject')}
          </Button>
          <Button
            size="small"
            variant="soft"
            color="success"
            startIcon={<Check />}
            loading={accepting}
            loadingPosition="start"
            disabled={rejecting}
            onClick={e => handleAction(e, 'shared')}
            sx={{ minWidth: 'auto', px: 1.5 }}
          >
            {t('Accept')}
          </Button>
        </Stack>
      </Box>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
